import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Layers,
  Mic,
  Shield,
  ArrowRight,
  CheckCircle2,
  Stethoscope,
  Sparkles,
  FileCheck,
} from 'lucide-react';

interface Props {
  onStartCase: () => void;
}

const topBundles = [
  { name: 'Shoulder Arthroscopy – SLAP + Cuff + Biceps + AC', codes: '29807, 23420, 23430, 23120', count: 24 },
  { name: 'Total Knee Arthroplasty (TKA)', codes: '27447, 27435', count: 31 },
  { name: 'Carpal Tunnel Release (CTR)', codes: '64721', count: 18 },
  { name: 'Manipulation Under Anesthesia - Post TKA', codes: '27570', count: 8 },
  { name: 'Extensor Mechanism Repair - Quad/VMO', codes: '27385', count: 5 },
];

const evidencePhrases = [
  '"Arthroscopic evaluation confirmed Type II SLAP tear with…"',
  '"Biceps tendon subluxation noted, tenodesis performed…"',
  '"Severe tricompartmental osteoarthritis requiring total arthroplasty…"',
  '"Assistant surgeon utilized and essential for positioning, retraction…"',
];

const payerRules = [
  { payer: 'Anthem/BCBS', rules: 12, status: 'Synced' },
  { payer: 'UnitedHealthcare', rules: 9, status: 'Synced' },
  { payer: 'Ohio BWC', rules: 14, status: 'Synced' },
  { payer: 'Aetna', rules: 7, status: 'Synced' },
];

export function SurgeonWelcomeScreen({ onStartCase }: Props) {
  return (
    <div className="space-y-6">
      {/* Hero Welcome */}
      <div className="relative overflow-hidden rounded-2xl border border-primary/20 p-8 bg-gradient-to-br from-card via-card to-primary/[0.04]">
        <div className="relative z-10 max-w-2xl space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">Your profile is ready</Badge>
          </div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            Welcome, Dr. Larkin.
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            We've pre-configured your profile based on your last <span className="text-foreground font-semibold">100 cases</span>. 
            Your clinical patterns have been analyzed, your CPT bundles are built, and your 
            hospital's authorization rules are synced. You're ready to start.
          </p>
        </div>
        {/* Decorative glow */}
        <div className="absolute right-0 top-0 w-96 h-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      </div>

      {/* Three summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Top 5 CPT Bundles */}
        <Card className="border-border col-span-1 md:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              <CardTitle className="text-sm">Your Top 5 CPT Bundles</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {topBundles.map((b, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{b.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{b.codes}</p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px] shrink-0">{b.count} cases</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Payer Rules */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <CardTitle className="text-sm">Auth Rules Synced</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {payerRules.map((p) => (
              <div key={p.payer} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-sm font-medium text-foreground">{p.payer}</p>
                  <p className="text-xs text-muted-foreground">{p.rules} rules</p>
                </div>
                <div className="flex items-center gap-1 text-xs text-primary">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {p.status}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Evidence Phrases */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm">Custom Intra-Op Evidence Phrases Mapped</CardTitle>
          </div>
          <p className="text-xs text-muted-foreground">
            LINK recognized these recurring clinical phrases from your dictations and will use them 
            to auto-populate documentation fields.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {evidencePhrases.map((phrase, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-secondary/50 border border-border">
                <FileCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground italic">{phrase}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-xl border border-primary/20 bg-primary/[0.03]">
        <div className="flex items-center gap-3">
          <Stethoscope className="w-6 h-6 text-primary" />
          <div>
            <p className="text-sm font-semibold text-foreground">Everything is set up.</p>
            <p className="text-xs text-muted-foreground">
              Your surgeon-first experience starts now — zero admin overhead.
            </p>
          </div>
        </div>
        <Button onClick={onStartCase} size="lg" className="gap-2 shrink-0">
          Start First Case <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
